import React, { useEffect, useState } from 'react';
import { getArtistSubscriptions, updateArtistSubscriptionStatus, ArtistSubscription } from '../services/artistSubscriptionService';
import { toast } from 'react-toastify';

interface ArtistSubscriptionListProps {
  onAssign?: () => void;
}

const ArtistSubscriptionList: React.FC<ArtistSubscriptionListProps> = ({ onAssign }) => {
  const [subs, setSubs] = useState<ArtistSubscription[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionId, setActionId] = useState<number | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getArtistSubscriptions();
      setSubs(data);
    } catch {
      toast.error('Failed to load subscriptions');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleStatus = async (sub: ArtistSubscription, action: 'renew' | 'deactivate') => {
    if (!window.confirm(`Are you sure you want to ${action} this subscription?`)) return;
    setActionId(sub.artistSubscriptionId);
    try {
      await updateArtistSubscriptionStatus(sub.artistSubscriptionId, action);
      toast.success(`Subscription ${action}d`);
      load();
    } catch { toast.error('Failed to update'); }
    finally { setActionId(null); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">Artist Subscriptions</h2>
        {onAssign && <button className="bg-primary text-white px-4 py-2 rounded" onClick={onAssign}>Assign Plan</button>}
      </div>
      {loading ? (<div className="text-center py-8">Loading...</div>) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-200 rounded-lg">
            <thead>
              <tr className="bg-background text-secondary">
                <th className="px-4 py-2">Artist</th>
                <th className="px-4 py-2">Plan</th>
                <th className="px-4 py-2">Start</th>
                <th className="px-4 py-2">End</th>
                <th className="px-4 py-2">Active</th>
                <th className="px-4 py-2">Admin Activated</th>
                <th className="px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {subs.map(s => (
                <tr key={s.artistSubscriptionId} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-2 font-semibold">{s.artistName || s.artistId}</td>
                  <td className="px-4 py-2">
                    <span className="font-medium">{s.plan?.planName}</span>
                    {s.plan?.isDefault && (<span className="ml-2 px-2 py-0.5 rounded bg-primary text-white text-xs">Default</span>)}
                    {s.plan?.price === 0 && (<span className="ml-2 px-2 py-0.5 rounded bg-green-200 text-green-800 text-xs">Free</span>)}
                  </td>
                  <td className="px-4 py-2 text-xs">{new Date(s.startDate).toLocaleDateString()}</td>
                  <td className="px-4 py-2 text-xs">{new Date(s.endDate).toLocaleDateString()}</td>
                  <td className="px-4 py-2">{s.isActive ? <span className="text-green-600 font-bold">Active</span> : <span className="text-gray-400">Inactive</span>}</td>
                  <td className="px-4 py-2">{s.activatedByAdmin ? <span className="text-primary font-bold">Yes</span> : <span className="text-gray-400">No</span>}</td>
                  <td className="px-4 py-2 flex gap-2">
                    {s.isActive ? (
                      <>
                        <button className="px-2 py-1 bg-secondary text-white rounded text-xs" disabled={actionId===s.artistSubscriptionId} onClick={() => handleStatus(s,'renew')}>Renew</button>
                        <button className="px-2 py-1 bg-red-100 text-red-700 border border-red-300 rounded text-xs" disabled={actionId===s.artistSubscriptionId} onClick={() => handleStatus(s,'deactivate')}>Deactivate</button>
                      </>
                    ) : (
                      <button className="px-2 py-1 bg-green-100 text-green-700 border border-green-300 rounded text-xs" disabled={actionId===s.artistSubscriptionId} onClick={() => handleStatus(s,'renew')}>Activate</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ArtistSubscriptionList;
